import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from '@/components/kibo-ui/dropzone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import {
  formatTag,
  useAddAssetTags,
  useCreateAsset,
  useCreateUploadBatch,
  useReanalyzeAsset,
  useRemoveAssetTags,
} from '@/features/assets';
import {
  claimAnalysisRetry,
  releaseAnalysisRetry,
  useAnalysisTrackerStore,
} from '@/features/assets/background-analysis';
import { useUploadFile } from '@/features/upload';
import { logError, logWarning } from '@/lib/log-error';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  ImageIcon,
  Loader2,
  Plus,
  Sparkles,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export interface UploadedVideo {
  id: string;
  file: File;
  objectUrl: string;
  status:
    | 'uploading'
    | 'processing'
    | 'analyzing'
    | 'complete'
    | 'error'
    | 'analysis_failed';
  progress: number;
  assetId?: string;
  uploadUrl?: string;
  tags: string[];
  error?: string;
}

export interface MassVideoUploadDialogProps {
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Callback when uploads complete successfully */
  onSuccess?: (assetIds: string[]) => void;
  /** Custom trigger element (uses DialogTrigger internally) */
  trigger?: React.ReactNode | null;
  /** Dialog title */
  title?: string;
  /** Dialog description */
  description?: string;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Default asset source — 'raw' enables AI tagging, 'edited' skips it */
  defaultSource?: 'raw' | 'edited';
}

const MAX_CONCURRENT_UPLOADS = 2;

const ACCEPTED_MEDIA_TYPES = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

// Wave animation keyframes injected via style tag
const waveKeyframes = `
@keyframes wave-text {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-3px); }
}
`;

function GeneratingBadge() {
  const text = 'Generating...';
  return (
    <>
      <style>{waveKeyframes}</style>
      <Badge variant="secondary" className="gap-1.5">
        <Sparkles className="size-3 text-amber-500" />
        <span className="flex">
          {text.split('').map((char, i) => (
            <span
              key={`${char}-${i}`}
              className="inline-block"
              style={{
                animation: 'wave-text 1.2s ease-in-out infinite',
                animationDelay: `${i * 50}ms`,
              }}
            >
              {char}
            </span>
          ))}
        </span>
      </Badge>
    </>
  );
}

interface VideoItemProps {
  video: UploadedVideo;
  onRemoveTag: (videoId: string, tag: string) => void;
  onAddTag: () => void;
  onRemove: () => void;
  onRetryUpload: () => void;
  onRetryAnalysis: () => void;
}

function VideoItem({
  video,
  onRemoveTag,
  onAddTag,
  onRemove,
  onRetryUpload,
  onRetryAnalysis,
}: VideoItemProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isImage = video.file.type.startsWith('image/');

  const handleMouseEnter = () => {
    // Hover-preview autoplay: AbortError (unmounted/paused before start) and
    // NotAllowedError (autoplay policy) are both benign here — swallow them.
    videoRef.current?.play().catch(() => {});
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <Card className="p-3">
      <div className="flex gap-4">
        {/* LEFT: Square Preview */}
        <div
          className="group relative size-[100px] shrink-0 overflow-hidden rounded-lg bg-muted"
          onMouseEnter={isImage ? undefined : handleMouseEnter}
          onMouseLeave={isImage ? undefined : handleMouseLeave}
        >
          {isImage ? (
            <img
              src={video.objectUrl}
              alt={video.file.name}
              className="absolute inset-0 size-full object-cover"
            />
          ) : (
            <video
              ref={videoRef}
              src={video.objectUrl}
              className="absolute inset-0 size-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={(e) => {
                e.currentTarget.currentTime = 0.001;
              }}
            />
          )}
          {/* Loading overlay for uploading state */}
          {(video.status === 'uploading' || video.status === 'processing') && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <Loader2 className="size-6 animate-spin text-white" />
            </div>
          )}
        </div>

        {/* RIGHT: Content Stack */}
        <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
          {/* Top: File name + status icon */}
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-sm font-medium">{video.file.name}</p>
            <div className="shrink-0">
              {video.status === 'complete' && (
                <CheckCircle2 className="size-4 text-green-600" />
              )}
              {(video.status === 'error' ||
                video.status === 'analysis_failed') && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="size-4" />
                </button>
              )}
              {(video.status === 'uploading' ||
                video.status === 'processing' ||
                video.status === 'analyzing') && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Middle: Progress bar for uploading/processing */}
          {(video.status === 'uploading' || video.status === 'processing') && (
            <div className="space-y-1">
              <Progress value={video.progress} className="h-1.5" />
              <p className="text-xs text-muted-foreground">
                {video.status === 'uploading'
                  ? `Uploading... ${video.progress}%`
                  : 'Processing...'}
              </p>
            </div>
          )}

          {/* Error state */}
          {video.status === 'error' && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="line-clamp-2 min-w-0 text-xs text-destructive">
                {video.error || 'Upload failed'}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shrink-0 px-2 text-xs"
                onClick={onRetryUpload}
              >
                Retry upload
              </Button>
            </div>
          )}
          {video.status === 'analysis_failed' && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="min-w-0 text-xs text-muted-foreground">
                Tagging failed. It's still in your library.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shrink-0 px-2 text-xs"
                onClick={onRetryAnalysis}
              >
                Retry tagging
              </Button>
            </div>
          )}

          {/* Bottom: Tags Section */}
          {(video.status === 'analyzing' || video.status === 'complete') && (
            <div className="mt-auto flex flex-wrap gap-1.5">
              {video.status === 'analyzing' ? (
                <GeneratingBadge />
              ) : (
                <>
                  {video.tags.length === 0 && (
                    <span className="text-xs text-muted-foreground">
                      No tags generated
                    </span>
                  )}
                  {video.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                      {formatTag(tag)}
                      <button
                        type="button"
                        onClick={() => onRemoveTag(video.id, tag)}
                        className="rounded-full hover:bg-muted-foreground/20"
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                  {/* Add tag button */}
                  <button
                    type="button"
                    onClick={onAddTag}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/50 px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <Plus className="size-3" />
                    Add
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

interface AddTagDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (tag: string) => void;
}

function AddTagDialog({ isOpen, onOpenChange, onSubmit }: AddTagDialogProps) {
  const [tagValue, setTagValue] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = tagValue.trim();
    if (trimmed) {
      onSubmit(trimmed);
      setTagValue('');
      onOpenChange(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTagValue('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add Tag</DialogTitle>
          <DialogDescription>
            Add a custom tag to help categorize this video.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Input
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              placeholder="Enter tag name"
              autoFocus
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!tagValue.trim()}>
              Add Tag
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MassVideoUploadDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onSuccess,
  trigger,
  title = 'Upload Videos',
  description = 'Upload multiple videos at once. Our AI will automatically generate tags for each video.',
  maxFiles = 50,
  maxSize = 600 * 1024 * 1024, // 600MB
  defaultSource = 'raw',
}: MassVideoUploadDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [uploadedVideos, setUploadedVideos] = useState<UploadedVideo[]>([]);
  const [assetSource, setAssetSource] = useState<'raw' | 'edited'>(
    defaultSource
  );
  const [addTagDialogState, setAddTagDialogState] = useState<{
    isOpen: boolean;
    videoId: string | null;
  }>({ isOpen: false, videoId: null });

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled
    ? (controlledOnOpenChange ?? (() => {}))
    : setInternalOpen;

  const mountedRef = useRef(true);

  const trackAnalysis = useAnalysisTrackerStore((state) => state.track);
  const untrackAnalysis = useAnalysisTrackerStore((state) => state.untrack);
  const trackedAnalyses = useAnalysisTrackerStore((state) => state.tracked);

  const { createAssetAsync } = useCreateAsset({ showToast: false });
  const { createBatchAsync } = useCreateUploadBatch();
  const { reanalyzeAssetAsync } = useReanalyzeAsset({ showToast: false });
  const { addTagsAsync } = useAddAssetTags({ showToast: false });
  const { removeTagsAsync } = useRemoveAssetTags({ showToast: false });
  const { uploadAsync: uploadFileAsync } = useUploadFile({
    purpose: 'org-asset',
    showToast: false,
  });

  const updateVideo = useCallback(
    (id: string, updates: Partial<Omit<UploadedVideo, 'id' | 'file'>>) => {
      setUploadedVideos((prev) =>
        prev.map((v) => (v.id === id ? { ...v, ...updates } : v))
      );
    },
    []
  );

  /**
   * Files still moving bytes. `analyzing` is deliberately EXCLUDED: this flag
   * blocks closing the dialog, and AI tagging now runs in the background, so
   * including it locked the user inside a modal for the whole analysis queue
   * (production has completed analyses 84 minutes after upload).
   */
  const hasActiveUploads = uploadedVideos.some(
    (v) => v.status === 'uploading' || v.status === 'processing'
  );

  // Cleanup object URLs on unmount. No polling timers to clear — analysis is
  // watched by `BackgroundAnalysisProvider`, which outlives this dialog on
  // purpose so closing it does not abandon the tagging.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional - capture videos at mount for cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const video of uploadedVideos) {
        URL.revokeObjectURL(video.objectUrl);
      }
    };
  }, []);

  // Hand analysis off to the app-wide tracker, so closing this dialog (or
  // leaving the page entirely) no longer abandons the tagging.
  useEffect(() => {
    const toTrack = uploadedVideos
      .filter((v) => v.status === 'analyzing' && v.assetId)
      .map((v) => ({ assetId: v.assetId as string, label: v.file.name }));

    if (toTrack.length > 0) trackAnalysis(toTrack);
  }, [uploadedVideos, trackAnalysis]);

  // Mirror tracker outcomes into local state so tags still stream in for a
  // user who kept the dialog open.
  useEffect(() => {
    for (const v of uploadedVideos) {
      if (!v.assetId) continue;
      const entry = trackedAnalyses[v.assetId];

      // A failed row follows the tracker too: a retry started elsewhere (the
      // background toast, the preview dialog) re-tracks the asset as pending.
      if (v.status === 'analysis_failed') {
        if (entry?.status === 'pending') {
          updateVideo(v.id, { status: 'analyzing', error: undefined });
        } else if (entry?.status === 'completed') {
          updateVideo(v.id, {
            status: 'complete',
            tags: entry.tags,
            error: undefined,
          });
        }
        continue;
      }
      if (v.status !== 'analyzing') continue;

      if (!entry || entry.status === 'pending') continue;

      if (entry.status === 'completed') {
        updateVideo(v.id, { status: 'complete', tags: entry.tags });
      } else if (entry.status === 'failed') {
        // The worker already reported the real cause to Sentry; warn here to
        // avoid duplicate error noise.
        logWarning('upload.analysisFailed', 'Asset analysis failed', {
          feature: 'upload',
          extra: { assetId: v.assetId, videoId: v.id },
        });
        updateVideo(v.id, { status: 'analysis_failed' });
      } else {
        // Abandoned: upload succeeded and the video is usable; only the tagging
        // is unresolved. Not an error for the user.
        updateVideo(v.id, {
          status: 'complete',
          tags: [],
          error: 'Tag generation is taking longer than expected',
        });
      }
    }
  }, [trackedAnalyses, uploadedVideos, updateVideo]);

  const batchIdRef = useRef<string | null>(null);
  const hasShownCompletionToast = useRef(false);

  // Reset completion toast flag when new uploads start
  useEffect(() => {
    if (hasActiveUploads) {
      hasShownCompletionToast.current = false;
    }
  }, [hasActiveUploads]);

  // Show a single toast when all uploads are done
  useEffect(() => {
    const allSettled =
      uploadedVideos.length > 0 &&
      uploadedVideos.every(
        (v) =>
          v.status === 'complete' ||
          v.status === 'error' ||
          v.status === 'analysis_failed'
      );

    if (allSettled && !hasShownCompletionToast.current) {
      hasShownCompletionToast.current = true;
      const successCount = uploadedVideos.filter(
        (v) => v.status === 'complete' || v.status === 'analysis_failed'
      ).length;
      const errorCount = uploadedVideos.filter(
        (v) => v.status === 'error'
      ).length;

      if (errorCount === 0) {
        toast.success(
          `All ${successCount} ${successCount === 1 ? 'asset' : 'assets'} uploaded successfully`
        );
      } else {
        toast.warning(
          `${successCount} of ${uploadedVideos.length} uploaded. Retry the failed files below.`
        );
      }
    }
  }, [uploadedVideos]);

  const processVideo = useCallback(
    async (video: UploadedVideo) => {
      const { file, id: videoId } = video;
      const isImage = file.type.startsWith('image/');
      updateVideo(videoId, {
        status: 'uploading',
        error: undefined,
        progress: video.uploadUrl ? 50 : 0,
      });

      try {
        const uploadUrl = video.uploadUrl ?? (await uploadFileAsync(file)).url;
        updateVideo(videoId, { uploadUrl });
        updateVideo(videoId, { progress: 50, status: 'processing' });

        // Create asset (with batchId if available)
        const asset = await createAssetAsync({
          file,
          blobUrl: uploadUrl,
          type: isImage ? 'image' : 'video',
          source: assetSource,
          batchId: batchIdRef.current ?? undefined,
        });
        updateVideo(videoId, { progress: 75, assetId: asset.id });

        if (assetSource === 'raw') {
          try {
            await reanalyzeAssetAsync(asset.id);
          } catch (error) {
            updateVideo(videoId, {
              status: 'analysis_failed',
              error:
                error instanceof Error
                  ? error.message
                  : 'Tag generation failed',
            });
            return;
          }
          updateVideo(videoId, {
            progress: 100,
            status: 'analyzing',
            assetId: asset.id,
          });
        } else {
          // Edited/polished assets skip analysis — mark complete immediately
          updateVideo(videoId, {
            progress: 100,
            status: 'complete',
            assetId: asset.id,
          });
        }
      } catch (error) {
        logError('upload.processVideo', error, {
          feature: 'upload',
          extra: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          },
        });
        updateVideo(videoId, {
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    },
    [
      uploadFileAsync,
      createAssetAsync,
      reanalyzeAssetAsync,
      updateVideo,
      assetSource,
    ]
  );

  // Process files with concurrency limit to avoid freezing on mobile
  const uploadQueueRef = useRef<UploadedVideo[]>([]);
  const activeUploadsRef = useRef(0);

  const drainQueue = useCallback(() => {
    while (
      activeUploadsRef.current < MAX_CONCURRENT_UPLOADS &&
      uploadQueueRef.current.length > 0
    ) {
      const video = uploadQueueRef.current.shift();
      if (!video) continue;
      activeUploadsRef.current++;
      processVideo(video).finally(() => {
        activeUploadsRef.current--;
        drainQueue();
      });
    }
  }, [processVideo]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // Create a batch if this is the first drop and no batch exists yet
      if (!batchIdRef.current && acceptedFiles.length > 0) {
        try {
          const batch = await createBatchAsync({
            totalAssets: acceptedFiles.length,
          });
          batchIdRef.current = batch.id;
        } catch {
          // Proceed without batch - face grouping won't work but uploads still function
        }
      }

      const videos: UploadedVideo[] = acceptedFiles.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        objectUrl: URL.createObjectURL(file),
        status: 'uploading',
        progress: 0,
        tags: [],
      }));
      setUploadedVideos((prev) => [...prev, ...videos]);
      uploadQueueRef.current.push(...videos);
      drainQueue();
    },
    [drainQueue, createBatchAsync]
  );

  const retryUpload = (video: UploadedVideo) => {
    if (video.status !== 'error') return;
    updateVideo(video.id, { status: 'uploading', error: undefined });
    uploadQueueRef.current.push(video);
    drainQueue();
  };

  const retryAnalysis = async (video: UploadedVideo) => {
    if (video.status !== 'analysis_failed' || !video.assetId) return;
    const { assetId } = video;
    if (!claimAnalysisRetry(assetId)) return;
    // Drop the stale `failed` entry first; the row stays `analysis_failed`
    // (not `processing`) so the upload-completion toast is not re-armed.
    untrackAnalysis([assetId]);
    try {
      await reanalyzeAssetAsync(assetId);
      trackAnalysis([{ assetId, label: video.file.name }]);
      updateVideo(video.id, { status: 'analyzing', error: undefined });
    } catch (error) {
      untrackAnalysis([assetId]);
      updateVideo(video.id, {
        status: 'analysis_failed',
        error: error instanceof Error ? error.message : 'Tag generation failed',
      });
    } finally {
      releaseAnalysisRetry(assetId);
    }
  };

  const removeVideo = useCallback(
    (id: string) => {
      setUploadedVideos((prev) => {
        const video = prev.find((v) => v.id === id);
        if (video) {
          URL.revokeObjectURL(video.objectUrl);
        }
        if (video?.assetId) untrackAnalysis([video.assetId]);
        return prev.filter((v) => v.id !== id);
      });
    },
    [untrackAnalysis]
  );

  const handleRemoveTag = useCallback(
    async (videoId: string, tag: string) => {
      const video = uploadedVideos.find((v) => v.id === videoId);
      if (!video?.assetId) return;

      try {
        await removeTagsAsync({ assetId: video.assetId, tags: [tag] });
        updateVideo(videoId, {
          tags: video.tags.filter((t) => t !== tag),
        });
      } catch {
        // Toast error is handled by the hook
      }
    },
    [uploadedVideos, removeTagsAsync, updateVideo]
  );

  const openAddTagDialog = (videoId: string) => {
    setAddTagDialogState({ isOpen: true, videoId });
  };

  const handleAddTagSubmit = async (tag: string) => {
    if (!addTagDialogState.videoId) return;

    const video = uploadedVideos.find(
      (v) => v.id === addTagDialogState.videoId
    );
    if (!video?.assetId) return;

    try {
      await addTagsAsync({ assetId: video.assetId, tags: [tag] });
      updateVideo(addTagDialogState.videoId, {
        tags: [...video.tags, tag],
      });
    } catch {
      // Toast error is handled by the hook
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing while uploads are in progress
    if (!newOpen && hasActiveUploads) return;

    if (!newOpen && uploadedVideos.length > 0) {
      // When closing, call onSuccess with completed asset IDs
      // Anything that reached the server counts, including rows still being
      // tagged — the asset exists and is usable, and its tags will be filled
      // in by the background tracker.
      const completedAssetIds = uploadedVideos
        .filter((v) => v.assetId)
        .map((v) => v.assetId as string);

      if (completedAssetIds.length > 0) {
        onSuccess?.(completedAssetIds);
      }

      // Clear LOCAL state when closing. The analysis tracker is deliberately
      // left alone: the whole point is that tagging outlives this dialog.
      setUploadedVideos([]);
      batchIdRef.current = null;
      setAssetSource(defaultSource);
    }
    setOpen(newOpen);
  };

  const completedCount = uploadedVideos.filter(
    (v) => v.status === 'complete'
  ).length;
  const processingCount = uploadedVideos.filter(
    (v) =>
      v.status === 'uploading' ||
      v.status === 'processing' ||
      v.status === 'analyzing'
  ).length;

  const defaultTrigger = (
    <Button>
      <Upload className="size-4" />
      Upload Videos
    </Button>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {trigger === null ? null : trigger !== undefined ? (
          <DialogTrigger asChild>{trigger}</DialogTrigger>
        ) : (
          <DialogTrigger asChild>{defaultTrigger}</DialogTrigger>
        )}
        <DialogContent
          className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-[600px]"
          onEscapeKeyDown={(e) => {
            if (uploadedVideos.length > 0) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (uploadedVideos.length > 0) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
            {/* Raw footage toggle — controls whether AI tagging runs */}
            <div className="flex items-center justify-between rounded-lg border px-4 py-3">
              <div className="space-y-0.5">
                <Label
                  htmlFor="raw-footage-toggle"
                  className="text-sm font-medium"
                >
                  Raw footage
                </Label>
                <p className="text-xs text-muted-foreground">
                  {assetSource === 'raw'
                    ? 'AI will auto-generate tags for each upload'
                    : 'Uploads will be stored without AI tagging'}
                </p>
              </div>
              <Switch
                id="raw-footage-toggle"
                checked={assetSource === 'raw'}
                onCheckedChange={(checked) =>
                  setAssetSource(checked ? 'raw' : 'edited')
                }
                disabled={uploadedVideos.length > 0}
              />
            </div>

            {/* Dropzone */}
            <Dropzone
              accept={ACCEPTED_MEDIA_TYPES}
              maxFiles={maxFiles}
              maxSize={maxSize}
              onDrop={handleDrop}
              className={cn(
                'min-h-[160px] whitespace-normal border-2 border-dashed',
                uploadedVideos.length > 0 && 'min-h-[120px]'
              )}
            >
              <DropzoneEmptyState>
                <div className="flex flex-col items-center gap-3">
                  <div className="rounded-full bg-muted p-3">
                    <div className="flex gap-1">
                      <Video className="size-5 text-muted-foreground" />
                      <ImageIcon className="size-5 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="font-medium">Drop your files here</p>
                    <p className="text-sm text-muted-foreground">
                      Videos (MP4, MOV, WebM) and images (JPG, PNG, WebP)
                    </p>
                  </div>
                </div>
              </DropzoneEmptyState>
              <DropzoneContent />
            </Dropzone>

            {/* Video List */}
            {uploadedVideos.length > 0 && (
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
                {/* Status Summary */}
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {completedCount} of {uploadedVideos.length} processed
                  </Badge>
                  {processingCount > 0 && (
                    <Badge variant="outline">
                      <Loader2 className="mr-1 size-3 animate-spin" />
                      {processingCount} in progress
                    </Badge>
                  )}
                </div>

                {/* Video Items */}
                <div className="min-h-[120px] flex-1 space-y-2 overflow-y-auto pr-1">
                  {uploadedVideos.map((video) => (
                    <VideoItem
                      key={video.id}
                      video={video}
                      onRemoveTag={handleRemoveTag}
                      onAddTag={() => openAddTagDialog(video.id)}
                      onRemove={() => removeVideo(video.id)}
                      onRetryUpload={() => retryUpload(video)}
                      onRetryAnalysis={() => void retryAnalysis(video)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={hasActiveUploads}
              onClick={() => handleOpenChange(false)}
            >
              {uploadedVideos.length > 0 ? 'Done' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tag Dialog */}
      <AddTagDialog
        isOpen={addTagDialogState.isOpen}
        onOpenChange={(newOpen) =>
          setAddTagDialogState((s) => ({ ...s, isOpen: newOpen }))
        }
        onSubmit={handleAddTagSubmit}
      />
    </>
  );
}
